import styled from 'styled-components';

const CellText = styled.div`
  font-size: 14px;
  text-decoration: ${props => props.isFinished && 'line-through'};
  color: ${props => props.isFinished && props.theme.color.label};
`;

const EmptyBlock = styled.div`
  border-radius: 4px;
  border: solid 1px ${props => props.theme.color.border};
  height: 640px;
  position: relative;
  text-align: center;
  > div {
    position: absolute;
    transform: translateY(-50%);
    top: 50%;
    width: 100%;
    p {
      font-size: 18px;
      color: ${p => p.theme.color.label};
      margin-top: 25px;
    }
  }
`;

const FullName = styled.div`
  margin-left: 0.8em;
  text-align: left;
  color: ${props => (props.isActive ? props.theme.color.primary : props.theme.color.text)};
  color: ${props => props.isFinished && props.theme.color.label};
  text-decoration: none;
  & > span {
    text-transform: capitalize;
    margin-right: 0.3em;
    display: inline-block;
    &:first-of-type {
      text-transform: uppercase;
      font-weight: bold;
    }
  }
`;

const GreenCircle = styled.label`
  left: -0.2em;
  position: absolute;
  transform: translateY(50%);
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 100%;
  &.Validated {
    background-color: #3accac;
  }

  &.NotValidated {
    background-color: ${(p) => p.theme.color.gold};
  }

  &.Blocked {
    background-color: ${props => props.theme.color.error};
  }
`;

const New = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #24ac8d;
`;

const PersonInfo = styled.div`
  margin-left: 0.8em;
  text-align: left;
  font-size: 14px;
  color: ${props => props.theme.color.label};
`;

const StatusButton = styled.button`
  border-radius: 4px;
  border: 2px solid
    ${props => (props.disabled ? props.theme.color.disabled : props.theme.color.primary)};
  color: ${props => (props.disabled ? props.theme.color.disabled : props.theme.color.primary)};
  padding: 7px;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${props => (props.disabled ? '' : 'cursor: pointer')};
`;

const Table = styled.table`
  width: 100%;
  display: table;
  border-spacing: 0;
  thead {
    border-collapse: collapse;
  }
  th {
    font-weight: normal;
    color: ${props => props.theme.color.label};
    max-width: 160px;
    padding: 17px 0px;
    font-size: 14px;
    border-collapse: collapse;
    background-color: ${p => p.theme.color.background};
  }

  tr th,
  tr td {
    border-right: 1px solid ${props => props.theme.color.border};
    border-bottom: 1px solid ${props => props.theme.color.border};
    text-align: center;
  }
  tr th:first-child,
  tr td:first-child {
    border-left: 1px solid ${props => props.theme.color.border};
  }
  tr th {
    border-top: 1px solid ${props => props.theme.color.border};
  }

  /* top-left border-radius */
  tr:first-child th:first-child {
    border-top-left-radius: 6px;
  }

  /* top-right border-radius */
  tr:first-child th:last-child {
    border-top-right-radius: 6px;
  }

  /* bottom-left border-radius */
  tr:last-child td:first-child {
    border-bottom-left-radius: 6px;
  }

  /* bottom-right border-radius */
  tr:last-child td:last-child {
    border-bottom-right-radius: 6px;
  }

  /* ──── tool-tip ────────────────────────────────────────────────────────────────────────── */
  .__react_component_tooltip {
    max-width: 350px;
    padding: 1em;
    border-color: ${props => props.theme.color.border}!important;
    &:before {
      border-top-color: ${props => props.theme.color.border}!important;
    }
  }
`;

const TableRow = styled.tr`
  vertical-align: middle;
  border-collapse: collapse;
  transition: background-color 1s;
  :hover {
    cursor: pointer;
  }

  td {
    padding: 0em 1em;
    box-sizing: border-box;
    position: relative;
  }

  td div {
    color: ${props => props.theme.color.label};
  }

  .time {
    width: 5%;
    padding: 0;
    position: relative;
    .delay-timer {
      bottom: -0.2em;
    }
  }

  .patient {
    min-width: 170px;
    width: 30%;
  }

  .ap-type {
    width: 8%;
  }

  .note {
    text-align: left;
    width: 30%;
    padding: 0 1em;
  }

  .certificate {
    width: 4%;
    min-width: 57px;
  }

  .triggers {
    text-align: center;
    width: 14%;
  }

  /* booked */
  &.booked {
    td div {
      color: ${props => props.theme.color.text};
    }
  }
  /* arrived */
  &.arrived {
    background-color: ${props => props.theme.color.veryLightBlue};
    .time-line {
      background-color: #6aa6eb;
    }
    td div {
      color: ${props => props.theme.color.text};
    }
  }
  /* in-progress */
  &.in-progress {
    background-color: ${props => props.theme.color.veryLightRed};
    .time-line {
      background-color: #ef5350;
    }
    td div {
      color: ${props => props.theme.color.text};
    }
  }
  /* to-radiology */
  &.to-radiology {
    background-color: ${props => props.theme.color.veryLightPurple};
    .time-line {
      background-color: ${props => props.theme.color.purple};
    }
    td div {
      color: ${props => props.theme.color.text};
    }
  }
  /* from-radiology */
  &.from-radiology {
    background-color: ${props => props.theme.color.veryLightBlue};
    .time-line {
      background-color: #6aa6eb;
    }
    td div {
      color: ${props => props.theme.color.text};
    }
  }
  /* restart-consultation */
  &.restart-consultation {
    background-color: ${props => props.theme.color.veryLightGold};
    .time-line {
      background-color: ${props => props.theme.color.gold};
    }
    td div {
      color: ${props => props.theme.color.text};
    }
  }
  /* finished */
  &.finished {
    background-color: ${props => props.theme.color.veryLightGreen};
    .time-line {
      background-color: #3accac;
    }
    td div {
      color: ${props => props.theme.color.text};
    }
  }
  /* paid */
  &.paid {
    .time-line {
      background-color: #e9eaed;
    }
    td div:not(.personal-info) {
      text-decoration: line-through;
      color: ${props => props.theme.color.label};
    }
  }
  /* canceled */
  &.cancelled {
    td div {
      color: ${props => props.theme.color.error};
    }
    .time-line + div {
      color: ${props => props.theme.color.text};
    }
  }
  td div.personal-info {
    color: ${props => props.theme.color.label};
  }
  &.updated-status {
    background-color: #fff5e3;
  }
`;

const Time = styled.div`
  text-align: center;
  line-height: 110%;
  padding: 0 12px;
  font-size: 18px;
  display: flex;
  align-items: center;
  min-height: 3.3em;
  height: 3.3em;
  position: relative;
  justify-content: center;
  span {
    display: inline-block;
    width: 100%;
    white-space: nowrap;
  }
`;

const TimeLine = styled.div`
  width: 8px;
  height: 59px;
  position: absolute;
  top: 0;
  left: 0;
`;

export {
  CellText,
  EmptyBlock,
  FullName,
  GreenCircle,
  New,
  PersonInfo,
  StatusButton,
  Table,
  TableRow,
  Time,
  TimeLine
};
